import React from "react";
import {
	ExtendedFormTemplate,
	FormField,
	FieldType,
	FieldTypeEnum,
} from "@/dentlab/src/pages/form-builder/types/formTypes";
import { FormSection } from "@/dentlab/src/pages/form-builder/types/formTypes";
import Odontogram from "@/dentlab/src/components/odontogram/Odontogram";
import { CustomFormFileUploadField } from "./custom-form-file-upload-field.component";
import {
	CustomFormData,
	CustomFormDataValue,
	FieldValueType,
	ToothData,
	AppointmentData,
} from "../types";
import FormPreviewField from "@/dentlab/src/pages/form-builder/components/form-preview/FormPreviewField";
import { Input } from "@/dentlab/src/components/ui/input";
import { Label } from "@/dentlab/src/components/ui/label";
import { Textarea } from "@/dentlab/src/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/dentlab/src/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/dentlab/src/components/ui/radio-group";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";

// Component to render a form field based on its type
export const FormFieldRenderer: React.FC<{
	field: FormField;
	value: CustomFormDataValue | undefined;
	onChange: (value: FieldValueType) => void;
}> = ({ field, value, onChange }) => {
	const {
		type,
		label,
		placeholder,
		required,
		options = [],
		description,
	} = field;

	// For title and description fields, we don't need to wrap them in a card
	if (type === "title") {
		return (
			<h3 className="text-lg font-semibold px-4">
				{label}
			</h3>
		);
	}

	if (type === "description") {
		return (
			<p className="text-sm text-gray-600 px-4">
				{label}
			</p>
		);
	}

	// Extract the actual field value from the CustomFormDataValue if it exists
	const fieldValue = value?.fieldValue;

	// Wrap all other field types in a Paper component for card-like appearance
	return (
		<div className="px-4 pb-2 border border-gray-200 rounded-lg bg-white">
			{renderFieldContent(
				type,
				label,
				placeholder,
				required,
				options,
				fieldValue,
				onChange,
				field,
				description
			)}
		</div>
	);
};

// Helper function to render the actual field content
const renderFieldContent = (
	type: FieldType,
	label: string,
	placeholder: string | undefined,
	required: boolean | undefined,
	options: string[],
	value: FieldValueType,
	onChange: (value: FieldValueType) => void,
	field: FormField,
	description?: string
) => {
	const renderFieldDescription = () => {
		if (description) {
			return (
				<p className="text-sm text-gray-600 mb-1">
					{description}
				</p>
			);
		}
		return null;
	};

	switch (type) {
		case FieldTypeEnum.TEXT:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<Input
						placeholder={placeholder}
						required={required}
						value={(value as string) || ""}
						onChange={(e) => onChange(e.target.value)}
						className="mt-1"
					/>
				</div>
			);

		case FieldTypeEnum.TEXTAREA:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<Textarea
						placeholder={placeholder}
						required={required}
						value={(value as string) || ""}
						onChange={(e) => onChange(e.target.value)}
						rows={4}
						className="mt-1"
					/>
				</div>
			);

		case FieldTypeEnum.RADIO: {
			const valueStr = (value as string) || "";
			const isOther =
				!!field.allowOther &&
				valueStr !== "" &&
				!options.includes(valueStr);
			const otherText = isOther ? valueStr : "";

			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<RadioGroup
						value={isOther ? "" : valueStr}
						onValueChange={(val) => onChange(val)}
						className="mt-2"
					>
						{options.map((option) => (
							<div key={option} className="flex items-center space-x-2">
								<RadioGroupItem value={option} id={`${field.id}-${option}`} />
								<Label
									htmlFor={`${field.id}-${option}`}
									className="text-sm font-normal cursor-pointer"
								>
									{option}
								</Label>
							</div>
						))}
					</RadioGroup>
					{field.allowOther && (
						<Input
							value={otherText}
							onChange={(e) => onChange(e.target.value)}
							placeholder="Sonstiges – eigene Antwort…"
							className={`mt-2 max-w-sm h-8 transition-colors ${
								isOther
									? "border-blue-600 bg-blue-50 text-blue-900"
									: ""
							}`}
							aria-label="Sonstiges Freitext"
							data-testid={`${field.id}-sonstiges-text`}
						/>
					)}
				</div>
			);
		}

		case FieldTypeEnum.CHIPS: {
			const isMulti = !!field.multi;
			const optionSet = new Set(options);

			const valueArr: string[] = isMulti
				? Array.isArray(value)
					? (value as string[])
					: []
				: [];
			const valueStr = !isMulti && typeof value === "string" ? value : "";

			const otherText = field.allowOther
				? isMulti
					? valueArr.find((v) => !optionSet.has(v)) ?? ""
					: valueStr && !optionSet.has(valueStr)
					? valueStr
					: ""
				: "";
			const isOther = !!otherText;

			const isChipSelected = (option: string) =>
				isMulti ? valueArr.includes(option) : valueStr === option;

			const toggleChip = (option: string) => {
				if (isMulti) {
					const next = valueArr.includes(option)
						? valueArr.filter((v) => v !== option)
						: [...valueArr, option];
					onChange(next as FieldValueType);
				} else {
					onChange(option);
				}
			};

			const handleOtherTextChange = (text: string) => {
				if (isMulti) {
					const withoutOther = valueArr.filter((v) =>
						optionSet.has(v)
					);
					onChange(
						(text ? [...withoutOther, text] : withoutOther) as FieldValueType
					);
				} else {
					onChange(text);
				}
			};

			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<div className="flex flex-wrap gap-2 mt-2">
						{options.map((option) => {
							const selected = isChipSelected(option);
							return (
								<button
									type="button"
									key={option}
									onClick={() => toggleChip(option)}
									className={`rounded-full border px-3 py-1 text-sm transition-colors ${
										selected
											? "bg-blue-600 border-blue-600 text-white"
											: "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
									}`}
									aria-pressed={selected}
									aria-label={option}
									data-testid={`${field.id}-chip-${option}`}
								>
									{option}
								</button>
							);
						})}
						{field.allowOther && (
							<div
								className={`rounded-full border flex items-center pl-3 pr-3 py-0.5 text-sm transition-colors ${
									isOther
										? "bg-blue-600 border-blue-600 text-white"
										: "bg-white border-gray-300 text-gray-700"
								}`}
								data-testid={`${field.id}-chip-sonstiges`}
							>
								<input
									type="text"
									value={otherText}
									onChange={(e) =>
										handleOtherTextChange(e.target.value)
									}
									placeholder="Sonstiges…"
									className={`bg-transparent border-0 outline-none text-sm py-0.5 min-w-[100px] w-32 ${
										isOther
											? "text-white placeholder-white/60"
											: "text-gray-700 placeholder-gray-400"
									}`}
									aria-label="Sonstiges Freitext"
									data-testid={`${field.id}-sonstiges-text`}
								/>
							</div>
						)}
					</div>
				</div>
			);
		}

		case FieldTypeEnum.CHECKBOX:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<div className="mt-2 space-y-2">
						{options.map((option) => {
							const isChecked = Array.isArray(value)
								? value.includes(option as never)
								: false;
							return (
								<div key={option} className="flex items-center space-x-2">
									<Checkbox
										id={`${field.id}-${option}`}
										checked={isChecked}
										onCheckedChange={(checked) => {
											const newValue = Array.isArray(value)
												? [...value]
												: [];
											if (checked) {
												newValue.push(option);
											} else {
												const index = newValue.indexOf(option);
												if (index > -1) {
													newValue.splice(index, 1);
												}
											}
											onChange(newValue as FieldValueType);
										}}
									/>
									<Label
										htmlFor={`${field.id}-${option}`}
										className="text-sm font-normal cursor-pointer"
									>
										{option}
									</Label>
								</div>
							);
						})}
					</div>
				</div>
			);

		case FieldTypeEnum.SELECT:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<Select
						value={(value as string) || ""}
						onValueChange={(val) => onChange(val)}
					>
						<SelectTrigger className="mt-1">
							<SelectValue placeholder="Bitte auswählen" />
						</SelectTrigger>
						<SelectContent>
							{options.map((option) => (
								<SelectItem key={option} value={option}>
									{option}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			);

		case FieldTypeEnum.DATE:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<Input
						type="date"
						required={required}
						value={(value as string) || ""}
						onChange={(e) => onChange(e.target.value)}
						className="mt-1"
					/>
				</div>
			);

		case FieldTypeEnum.TIME:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<Input
						type="time"
						required={required}
						value={(value as string) || ""}
						onChange={(e) => onChange(e.target.value)}
						className="mt-1"
					/>
				</div>
			);

		case FieldTypeEnum.ODONTOGRAM:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<div className="mt-2">
						<Odontogram
							selectedTeeth={value as ToothData[]}
							onTeethChange={onChange}
							maxSelections={
								field.defaultValue
									? Array.isArray(field.defaultValue)
										? field.defaultValue.map(Number).length
										: undefined
									: undefined
							}
							odontogramFields={field.odontogramFields}
						/>
					</div>
				</div>
			);

		case FieldTypeEnum.FILE:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<div className="mt-2">
						<CustomFormFileUploadField
							value={value}
							onChange={onChange}
							required={required}
						/>
					</div>
				</div>
			);

		case FieldTypeEnum.APPOINTMENT:
			return (
				<div className="my-4">
					<Label className="text-sm font-medium">
						{label}
						{required && <span className="text-red-500 ml-1">*</span>}
					</Label>
					{renderFieldDescription()}
					<FormPreviewField
						field={field}
						value={value as AppointmentData[]}
						onChange={onChange}
					/>
				</div>
			);

		// Other field types can be implemented as needed
		default:
			return (
				<p className="text-red-600 my-4">
					Feldtyp {type} nicht unterstützt
				</p>
			);
	}
};

// Component to render a section of the form
export const FormSectionRenderer: React.FC<{
	section: FormSection;
	formData: CustomFormData;
	onChange: (fieldId: string, value: FieldValueType) => void;
}> = ({ section, formData, onChange }) => {
	return (
		<div className="flex flex-col gap-2 mb-12">
			<div className="p-4 border border-gray-200 rounded-lg bg-white">
				<h2 className="text-xl font-semibold">
					{section.title}
				</h2>
				{section.description && (
					<p className="text-sm text-gray-600 mt-1">
						{section.description}
					</p>
				)}
			</div>

			{section.fields.map((field) => (
				<FormFieldRenderer
					key={field.id}
					field={field}
					value={formData[field.id]}
					onChange={(value) => onChange(field.id, value)}
				/>
			))}
		</div>
	);
};

// Main component for rendering a custom form
interface CustomFormRendererProps {
	form: ExtendedFormTemplate;
	onFormDataChange: (formData: CustomFormData) => void;
	formData: CustomFormData;
}

const CustomFormRenderer: React.FC<CustomFormRendererProps> = ({
	form,
	onFormDataChange,
	formData,
}) => {
	const handleFieldChange = (fieldId: string, value: FieldValueType) => {
		// Find the field to get its type
		let fieldType: FieldType = "text"; // Default to text

		// Search through all sections to find the field
		for (const section of form.definition.sections) {
			const field = section.fields.find((f) => f.id === fieldId);
			if (field) {
				fieldType = field.type;
				break;
			}
		}

		const updatedFormData = {
			...formData,
			[fieldId]: {
				fieldType,
				fieldValue: value,
			},
		};
		onFormDataChange(updatedFormData);
	};

	const bgColor = form?.bg_color || "green-50";

	return (
		<div
			className={`p-6 bg-${bgColor} rounded-lg border border-gray-200 w-full max-w-[640px] mx-auto`}
		>
			<div className="p-5 mb-6 bg-white rounded-lg border border-gray-200">
				<h1 className="text-2xl font-semibold">
					{form.title}
				</h1>
				{form.description && (
					<p className="text-base text-gray-600 mt-2">
						{form.description}
					</p>
				)}
			</div>

			{form.definition.sections.map((section) => (
				<FormSectionRenderer
					key={section.id}
					section={section}
					formData={formData}
					onChange={handleFieldChange}
				/>
			))}
		</div>
	);
};

export default CustomFormRenderer;
